import { useEffect, useState } from "react";
import { Save, UploadCloud, Eye, Trash2, Loader2, Tag } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import LivePreview from "./LivePreview";

const LABELS = ["Published Version", "AI Generated", "Human Reviewed", "Seasonal Update", "Bonfire Night", "Christmas", "Promotion", "Emergency Rollback Point"];
const STATUS = {
  draft: { label: "Draft", cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  pending_review: { label: "Pending Review", cls: "border-sky-500/40 text-sky-300 bg-sky-500/10" },
  approved: { label: "Approved", cls: "border-green-500/40 text-green-300 bg-green-500/10" },
  published: { label: "Published", cls: "border-green-500/50 text-green-300 bg-green-500/10" },
  archived: { label: "Archived", cls: "border-white/20 text-white/50 bg-white/5" },
};

export default function DraftPublishBar({ entityType, entityId, entity, seo, onReload }) {
  const [status, setStatus] = useState("published");
  const [hasDraft, setHasDraft] = useState(false);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const [pubDialog, setPubDialog] = useState(false);
  const [label, setLabel] = useState("Published Version");

  const loadStatus = () => {
    if (!entityId) return;
    api.get("/admin/seo/content-status", { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => { setStatus(data.status || "published"); setHasDraft(!!data.has_draft); }).catch(() => {});
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [entityId, entityType]);

  const call = async (key, url, body, msg) => {
    setBusy(key);
    try {
      const { data } = await api.post(url, body);
      if (data.ok === false) { toast.error(data.error || "Failed."); return null; }
      toast.success(msg);
      loadStatus();
      return data;
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); return null; }
    finally { setBusy(""); }
  };

  const saveDraft = () => call("draft", "/admin/seo/content/save-draft", { entity_type: entityType, entity_id: entityId, seo }, "Draft saved (not public).");
  const discard = () => call("discard", "/admin/seo/content/discard-draft", { entity_type: entityType, entity_id: entityId }, "Draft discarded.");
  const doPublish = async () => {
    const r = await call("publish", "/admin/seo/content/publish", { entity_type: entityType, entity_id: entityId, seo, label }, "Published live.");
    if (r) { setPubDialog(false); onReload && onReload(); }
  };

  if (!entityId) return null;
  const s = STATUS[status] || STATUS.published;

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3 flex items-center gap-2 flex-wrap" data-testid="draft-publish-bar">
      <Badge variant="outline" className={`gap-1 ${s.cls}`} data-testid="content-status-badge">{s.label}</Badge>
      {hasDraft && <span className="text-[11px] text-amber-300" data-testid="has-draft-indicator">Unpublished draft exists</span>}
      <div className="flex-1" />
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setPreview(true)} data-testid="preview-draft-btn"><Eye className="h-4 w-4" /> Preview</Button>
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={saveDraft} disabled={busy === "draft"} data-testid="save-draft-btn">{busy === "draft" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Draft</Button>
      {hasDraft && <Button type="button" variant="ghost" size="sm" className="gap-1 text-red-400" onClick={discard} disabled={busy === "discard"} data-testid="discard-draft-btn"><Trash2 className="h-4 w-4" /> Discard</Button>}
      <Button type="button" size="sm" className="cf-gold-btn gap-1" onClick={() => setPubDialog(true)} data-testid="publish-btn"><UploadCloud className="h-4 w-4" /> Publish</Button>

      <LivePreview open={preview} onOpenChange={setPreview} entityType={entityType} entity={entity} seo={seo} />

      <Dialog open={pubDialog} onOpenChange={setPubDialog}>
        <DialogContent data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UploadCloud className="h-5 w-5 text-gold" /> Publish content</DialogTitle>
            <DialogDescription>This makes the current content live and records a version. Optionally add a quick label for the version history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-1"><Tag className="h-3 w-3" /> Quick version label (optional)</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} list="publish-labels" className="bg-black/30 border-white/15" data-testid="publish-label-input" />
            <datalist id="publish-labels">{LABELS.map((l) => <option key={l} value={l} />)}</datalist>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {LABELS.slice(0, 6).map((l) => <button key={l} type="button" onClick={() => setLabel(l)} className="text-[11px] rounded-full border border-white/15 text-white/60 hover:border-gold/40 hover:text-gold px-2.5 py-1">{l}</button>)}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPubDialog(false)}>Cancel</Button>
            <Button className="cf-gold-btn gap-1" onClick={doPublish} disabled={busy === "publish"} data-testid="publish-confirm-btn">{busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Publish Now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
